﻿import {lazy, ReactNode, Suspense} from 'react';
import {
    BrowserRouter as Router,
    Navigate,
    Route,
    Routes
} from 'react-router-dom';
import {toast, ToastContainer} from 'react-toastify';
import pages from './pages';
import MainLayout from './components/layout/MainLayout.tsx';
import {ConfirmProvider, SidePanelProvider} from './components/ds/index.ts';
import {
    AuthStateManagerProvider,
    useAuthStateManager
} from './hooks/useAuthStateManager.tsx';
import baseUri from './api/baseUri.ts';
import {useMutation} from 'react-query';
import {Authorization} from './types.ts';
import unknownErrorToString from './lib/unknownErrorToString.ts';
import useAuthMicrosoft from './api/auth/useAuthMicrosoft.ts';
import {MxMessagesHub} from './components/shared/MxMessagesHub.tsx';
import {GalleryStateProvider} from './components/projects/GalleryStateContext.tsx';
import {useEffectOnce} from './hooks/useEffectOnce.ts';
import {initializeAnalytics, useAnalytics} from './analytics/analytics.ts';
import useClientConfig from './hooks/useClientConfig.tsx';
import {TranslationProvider} from './components/translation/TranslationContext.tsx';
import SignInEndedNotice from './components/auth/SignInEndedNotice.tsx';
import {startSignIn} from './auth/authorizationServer.ts';
import {isRenderMode} from './lib/tabView.ts';

// The renderer's page (components/render), loaded only there.
const RenderRoot = lazy(() => import('./components/render/RenderRoot.tsx'));

interface ComponentWithChildrenProps {
    children: ReactNode;
}

const WithMsCallback: React.FC<ComponentWithChildrenProps> = ({children}) => {
    const {authorizationServer} = useClientConfig();

    const authenticateMicrosoftAuth = useAuthMicrosoft();
    const mutation = useMutation(authenticateMicrosoftAuth, {
        onSuccess: async () => {
            // The server's session is open: get the tokens through it (AuthCallbackPage takes it from there).
            await startSignIn(authorizationServer);
        },
        onError: (error: unknown) => {
            toast.error(unknownErrorToString(error));
        }
    });

    window.msCallback = async (accessToken: string) => {
        mutation.mutate({token: accessToken});
    };

    return children;
};

const WithAnalytics: React.FC<ComponentWithChildrenProps> = ({children}) => {
    const {gaTrackingCode} = useClientConfig();

    useEffectOnce(() => {
        initializeAnalytics(gaTrackingCode ?? 'UA-129458804-1');
    });

    useAnalytics();

    return children;
};

const AuthenticatedRoutes: React.FC = () => {
    const authStateManager = useAuthStateManager();

    return (
        <Router>
            <WithAnalytics>
                <SignInEndedNotice />
                <WithMsCallback>
                    <TranslationProvider>
                        <Suspense fallback={<div>Loading...</div>}>
                            <Routes>
                                {Object.entries(pages).flatMap(([key, page]) =>
                                    page.paths.map(path => (
                                        <Route
                                            key={`${key}-${path}`}
                                            path={path}
                                            element={
                                                authStateManager.isAuthorizedForResource(
                                                    page as Authorization
                                                ) ? (
                                                    <MainLayout page={page}>
                                                        <page.component />
                                                    </MainLayout>
                                                ) : (
                                                    <Navigate to="/" replace />
                                                )
                                            }
                                        />
                                    ))
                                )}
                            </Routes>
                        </Suspense>
                    </TranslationProvider>
                </WithMsCallback>
            </WithAnalytics>
        </Router>
    );
};

const App: React.FC = () => {
    if (isRenderMode()) {
        return (
            <Suspense fallback={null}>
                <RenderRoot />
            </Suspense>
        );
    }

    return (
        <AuthStateManagerProvider baseUri={baseUri}>
            <MxMessagesHub>
                {/* Toasts: bottom left, three at most, success clears after
                    six seconds and pauses on hover; errors stay (lib/notify). */}
                <ToastContainer
                    position="bottom-left"
                    theme="dark"
                    limit={3}
                    autoClose={6000}
                    hideProgressBar
                    closeButton={false}
                    icon={false}
                    pauseOnFocusLoss={false}
                />
                <ConfirmProvider>
                    <SidePanelProvider>
                        <GalleryStateProvider>
                            <AuthenticatedRoutes />
                        </GalleryStateProvider>
                    </SidePanelProvider>
                </ConfirmProvider>
            </MxMessagesHub>
        </AuthStateManagerProvider>
    );
};

export default App;
